import {
  Modal,
  Stack,
  Title,
  Button,
  useToast,
  useActions,
  Table,
  Tooltip,
  useTheme,
  Icon
} from "@introist/react-foundation/v2";
import { api, RouterOutput } from "../../../../../services/rpc/RpcProvider";
import { formatDate } from "../../../../../utils/DatesUtils";

type PendingRequest = RouterOutput["employees"]["v4"]["deletion"]["requests"]["list"][number];

interface PendingRemovalsModalProps {
  open: boolean;
  onClose: () => void;
  pendingRequests: PendingRequest[];
  onCancelAll: () => void | Promise<void>;
}

export const PendingRemovalsModal = ({
  open,
  onClose,
  pendingRequests,
  onCancelAll
}: PendingRemovalsModalProps) => {
  const { theme } = useTheme();
  const toast = useToast();
  const { onConfirmAction } = useActions();
  const cancelAllMutation = api.employees.v4.deletion.requests.cancelAll.useMutation();

  const handleCancelAll = onConfirmAction(
    async () => {
      await cancelAllMutation
        .mutateAsync({})
        .then(async () => {
          toast.success("All pending removals cancelled");
          await onCancelAll();
          onClose();
        })
        .catch(() => {
          toast.error("Failed to cancel pending removals");
        });
    },
    {
      title: "Cancel all pending removals",
      description: `Are you sure you want to cancel all ${pendingRequests.length} pending employee removals?`,
      confirmTitle: "Cancel all"
    }
  );

  return (
    <Modal open={open} onClose={onClose} title="Pending employee removals">
      <Stack vertical gap="large" style={{ width: "700px" }}>
        <Title>
          {pendingRequests.length} {pendingRequests.length === 1 ? "employee is" : "employees are"}{" "}
          scheduled for removal
        </Title>

        <Table
          data={pendingRequests}
          columns={[
            {
              key: "employeeName",
              title: "Employee",
              width: "30%",
              render: (request: PendingRequest) => (
                <Title>{request.employeeName || "Unknown"}</Title>
              )
            },
            {
              key: "scheduledExecutionDate",
              title: "Scheduled removal",
              width: "25%",
              render: (request: PendingRequest) => (
                <Title>{formatDate(request.scheduledExecutionDate) || "Unknown"}</Title>
              )
            },
            {
              key: "errorMessage",
              title: "Status",
              width: "45%",
              render: (request: PendingRequest) =>
                request.errorMessage ? (
                  <Tooltip tooltip={request.errorMessage}>
                    <Stack>
                      <Icon name="warning" color={theme.palette.danger.default} />
                      <Title
                        color={theme.palette.danger.default}
                        style={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap"
                        }}
                      >
                        {request.errorMessage}
                      </Title>
                    </Stack>
                  </Tooltip>
                ) : (
                  <Title color="success">Pending</Title>
                )
            }
          ]}
        />

        <Stack justifyContent="space-between">
          <Button variant="outlined" destructive onClick={handleCancelAll}>
            Cancel all removals
          </Button>
          <Button onClick={onClose}>Close</Button>
        </Stack>
      </Stack>
    </Modal>
  );
};
